// Gamification System - XP, Levels, Challenges, Combos

/**
 * XP Requirements for each level (exponential growth)
 */
export const XP_PER_LEVEL = (level) => {
  return Math.floor(100 * Math.pow(1.15, level - 1));
};

/**
 * Calculate total XP needed to reach a level
 */
export const getTotalXPForLevel = (level) => {
  let total = 0;
  for (let i = 1; i < level; i++) {
    total += XP_PER_LEVEL(i);
  }
  return total;
};

/**
 * Calculate level from total XP
 */
export const getLevelFromXP = (totalXP) => {
  let level = 1;
  let xpNeeded = 0;
  
  while (xpNeeded <= totalXP && level < 50) {
    xpNeeded += XP_PER_LEVEL(level);
    if (xpNeeded <= totalXP) {
      level++;
    }
  }
  
  return level;
};

/**
 * Get XP progress for current level
 */
export const getXPProgress = (totalXP) => {
  const level = getLevelFromXP(totalXP);
  const xpForPreviousLevels = getTotalXPForLevel(level);
  const currentLevelXP = totalXP - xpForPreviousLevels;
  const xpNeededForNextLevel = XP_PER_LEVEL(level);
  
  return {
    level,
    currentLevelXP,
    xpNeededForNextLevel,
    percentage: Math.min((currentLevelXP / xpNeededForNextLevel) * 100, 100),
    totalXP
  };
};

/**
 * XP Rewards for different actions
 */
export const XP_REWARDS = {
  CORRECT_ANSWER: 2, // Each correct answer = 2 XP
  PERFECT_QUIZ: 0, // No bonus for perfect quiz
  FIRST_QUIZ: 0, // No bonus for first quiz
  DAILY_STREAK: 0, // No bonus for streak
  BADGE_EARNED: 0, // No bonus for badges
  CHALLENGE_COMPLETE: 0, // No bonus for challenges
  COMBO_BONUS: 0, // No combo bonus
  SPEED_BONUS: 0, // No speed bonus
  DIFFICULTY_MULTIPLIER: {
    easy: 1,
    medium: 1,
    hard: 1
  }
};

/**
 * Calculate XP for a quiz
 * Practice Mode: Each correct answer = 2 XP (all difficulties)
 * Timed Mode: 
 *   - Easy: 3 XP per correct answer
 *   - Medium: 4 XP per correct answer
 *   - Hard: 5 XP per correct answer
 */
export const calculateQuizXP = (quizData) => {
  const { correctAnswers, quizMode = 'practice', difficulty = 'medium' } = quizData;
  
  let xpPerAnswer = 2; // Default for Practice mode
  
  if (quizMode === 'timed') {
    // Timed mode XP based on difficulty
    if (difficulty === 'easy') {
      xpPerAnswer = 3;
    } else if (difficulty === 'medium') {
      xpPerAnswer = 4;
    } else if (difficulty === 'hard') {
      xpPerAnswer = 5;
    }
  }
  
  const xp = correctAnswers * xpPerAnswer;
  
  return xp;
};

/**
 * Get level title based on level
 */
export const getLevelTitle = (level) => {
  if (level >= 50) return { title: 'Legendary Master', color: '#9b59b6', icon: '👑' };
  if (level >= 40) return { title: 'Grand Master', color: '#e74c3c', icon: '🏆' };
  if (level >= 30) return { title: 'Expert', color: '#f39c12', icon: '⭐' };
  if (level >= 20) return { title: 'Advanced', color: '#3498db', icon: '💎' };
  if (level >= 10) return { title: 'Intermediate', color: '#2ecc71', icon: '🌟' };
  return { title: 'Beginner', color: '#95a5a6', icon: '🌱' };
};

/**
 * Combo System
 */
export const calculateCombo = (consecutiveCorrect) => {
  if (consecutiveCorrect < 3) return 0;
  return Math.floor(consecutiveCorrect / 3);
};

export const getComboMultiplier = (combo) => {
  return 1 + (combo * 0.1); // 10% per combo level
};

export const getComboMessage = (combo) => {
  if (combo >= 5) return { text: 'LEGENDARY COMBO! 🔥', color: '#e74c3c' };
  if (combo >= 4) return { text: 'AMAZING COMBO! ⭐', color: '#f39c12' };
  if (combo >= 3) return { text: 'GREAT COMBO! 💫', color: '#3498db' };
  if (combo >= 2) return { text: 'COMBO! ✨', color: '#2ecc71' };
  return { text: '', color: '' };
};

/**
 * Daily Challenges
 */
export const DAILY_CHALLENGES = [
  {
    id: 'perfect_quiz',
    name: 'Perfect Score',
    description: 'Get 100% on any quiz',
    xpReward: 100,
    icon: '💯',
    color: '#2ecc71',
    check: (data) => data.score === data.total
  },
  {
    id: 'three_quizzes',
    name: 'Triple Threat',
    description: 'Complete 3 quizzes today',
    xpReward: 75,
    icon: '🎯',
    color: '#3498db',
    check: (data) => data.quizzesToday >= 3
  },
  {
    id: 'hard_mode',
    name: 'Hard Mode Master',
    description: 'Complete a quiz on Hard difficulty',
    xpReward: 80,
    icon: '🔥',
    color: '#e74c3c',
    check: (data) => data.difficulty === 'hard'
  },
  {
    id: 'timed_mode',
    name: 'Speed Demon',
    description: 'Complete a quiz in Timed mode',
    xpReward: 60,
    icon: '⏱️',
    color: '#f39c12',
    check: (data) => data.mode === 'timed'
  },
  {
    id: 'five_categories',
    name: 'Category Explorer',
    description: 'Play 5 different categories today',
    xpReward: 90,
    icon: '🌍',
    color: '#9b59b6',
    check: (data) => data.categoriesPlayed >= 5
  },
  {
    id: 'streak_active',
    name: 'Streak Keeper',
    description: 'Maintain your daily streak',
    xpReward: 50,
    icon: '🔥',
    color: '#e67e22',
    check: (data) => data.streakActive === true
  },
  {
    id: 'combo_master',
    name: 'Combo Master',
    description: 'Achieve a 5x combo',
    xpReward: 85,
    icon: '⚡',
    color: '#1abc9c',
    check: (data) => data.maxCombo >= 5
  }
];

/**
 * Get daily challenges (3 random per day)
 */
export const getDailyChallenges = (date = new Date()) => {
  const dateString = date.toISOString().split('T')[0];
  const seed = dateString.split('-').reduce((a, b) => parseInt(a) + parseInt(b), 0);
  
  // Shuffle challenges based on date seed
  const shuffled = [...DAILY_CHALLENGES].sort((a, b) => {
    const hashA = (seed + a.id.charCodeAt(0)) % 100;
    const hashB = (seed + b.id.charCodeAt(0)) % 100;
    return hashA - hashB;
  });
  
  return shuffled.slice(0, 3);
};

/**
 * Check if challenge is completed
 */
export const checkChallengeCompletion = (challenge, userData) => {
  return challenge.check(userData);
};

/**
 * Leaderboard System
 */
export const getLeaderboard = () => {
  try {
    const profiles = JSON.parse(localStorage.getItem('kidsLearningProfiles') || '[]');
    
    return profiles
      .filter(p => p.gamification?.totalXP > 0)
      .map(p => ({
        id: p.id,
        name: p.name,
        avatar: p.avatar,
        color: p.color,
        level: getLevelFromXP(p.gamification?.totalXP || 0),
        totalXP: p.gamification?.totalXP || 0,
        badges: p.earnedBadges?.length || 0,
        quizzes: Object.values(p.userProgress || {}).reduce((sum, cat) => sum + (cat.totalQuizzes || 0), 0)
      }))
      .sort((a, b) => b.totalXP - a.totalXP)
      .slice(0, 10);
  } catch (error) {
    console.error('Error loading leaderboard:', error);
    return [];
  }
};

/**
 * Get rank for a profile
 */
export const getProfileRank = (profileId) => {
  const leaderboard = getLeaderboard();
  const rank = leaderboard.findIndex(p => p.id === profileId) + 1;
  return rank || null;
};

/**
 * Save gamification data
 */
export const saveGamificationData = (profileId, gamificationData) => {
  try {
    const profiles = JSON.parse(localStorage.getItem('kidsLearningProfiles') || '[]');
    const profileIndex = profiles.findIndex(p => p.id === profileId);
    
    if (profileIndex !== -1) {
      profiles[profileIndex].gamification = gamificationData;
      localStorage.setItem('kidsLearningProfiles', JSON.stringify(profiles));
    }
  } catch (error) {
    console.error('Error saving gamification data:', error);
  }
};

/**
 * Load gamification data
 */
export const loadGamificationData = (profileId) => {
  try {
    const profiles = JSON.parse(localStorage.getItem('kidsLearningProfiles') || '[]');
    const profile = profiles.find(p => p.id === profileId);
    
    return profile?.gamification || {
      totalXP: 0,
      level: 1,
      dailyChallenges: {},
      todayStats: {
        quizzesToday: 0,
        categoriesPlayed: [],
        maxCombo: 0,
        lastPlayedDate: null
      }
    };
  } catch (error) {
    console.error('Error loading gamification data:', error);
    return {
      totalXP: 0,
      level: 1,
      dailyChallenges: {},
      todayStats: {
        quizzesToday: 0,
        categoriesPlayed: [],
        maxCombo: 0,
        lastPlayedDate: null
      }
    };
  }
};

/**
 * Update today's stats
 */
export const updateTodayStats = (profileId, updates) => {
  const data = loadGamificationData(profileId);
  const today = new Date().toISOString().split('T')[0];
  
  // Reset if new day
  if (data.todayStats.lastPlayedDate !== today) {
    data.todayStats = {
      quizzesToday: 0,
      categoriesPlayed: [],
      maxCombo: 0,
      lastPlayedDate: today
    };
    data.dailyChallenges = {}; // Reset challenges
  }
  
  // Update stats
  if (updates.quizCompleted) {
    data.todayStats.quizzesToday++;
  }
  
  if (updates.category && !data.todayStats.categoriesPlayed.includes(updates.category)) {
    data.todayStats.categoriesPlayed.push(updates.category);
  }
  
  if (updates.combo > data.todayStats.maxCombo) {
    data.todayStats.maxCombo = updates.combo;
  }
  
  saveGamificationData(profileId, data);
  return data;
};

/**
 * Award XP and check for level up
 */
export const awardXP = (profileId, xpAmount) => {
  const data = loadGamificationData(profileId);
  const oldLevel = getLevelFromXP(data.totalXP);
  
  data.totalXP += xpAmount;
  const newLevel = getLevelFromXP(data.totalXP);
  
  saveGamificationData(profileId, data);
  
  return {
    xpAwarded: xpAmount,
    totalXP: data.totalXP,
    leveledUp: newLevel > oldLevel,
    oldLevel,
    newLevel,
    progress: getXPProgress(data.totalXP)
  };
};

/**
 * Complete daily challenge
 */
export const completeDailyChallenge = (profileId, challengeId) => {
  const data = loadGamificationData(profileId);
  const today = new Date().toISOString().split('T')[0];
  
  if (!data.dailyChallenges[today]) {
    data.dailyChallenges[today] = [];
  }
  
  if (!data.dailyChallenges[today].includes(challengeId)) {
    data.dailyChallenges[today].push(challengeId);
    saveGamificationData(profileId, data);
    return true;
  }
  
  return false;
};

/**
 * Check if challenge is completed today
 */
export const isChallengeCompleted = (profileId, challengeId) => {
  const data = loadGamificationData(profileId);
  const today = new Date().toISOString().split('T')[0];
  
  return data.dailyChallenges[today]?.includes(challengeId) || false;
};
